import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { ImagePlus, X } from "lucide-react";
import {
  ApiError,
  formatPhotoIds,
  parsePhotoIds,
  QUESTIONNAIRE_PHOTOS_PER_ITEM,
} from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { Button } from "@ferrivia/ui";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import { PhotoPrepError, preparePhoto } from "../photos/preparePhoto";
import s from "./PhotoUploadField.module.css";

// Same single-instrument reasoning as SupplementPicker's INSTRUMENT.
const INSTRUMENT = "health_questionnaire" as const;

type UploadFailure = "not_image" | "too_large" | "limit_reached" | "network" | "unknown";

function failureFor(err: unknown): UploadFailure {
  if (err instanceof PhotoPrepError) return err.reason;
  if (err instanceof ApiError) {
    if (err.status === 415) return "not_image";
    if (err.status === 413) return "too_large";
    if (err.code === "photo_limit_reached") return "limit_reached";
    return "unknown";
  }
  return "network";
}

export interface PhotoUploadFieldProps {
  labelledBy: string;
  /** The repeating group the photos are uploaded for (`iron_supplements`). */
  questionKey: string;
  /** Comma-separated photo ids, the field's stored value. */
  value: string;
  onChange(value: string): void;
}

/** `iron_supplements.photos` / `other_supplement_items.photos`'s control
    (`field.control === "photo_upload"`): up to three photo tiles and an add
    tile. A picked file is re-encoded on the device (`preparePhoto`),
    uploaded straight away, and its id appended to the value; the item's own
    Tallenna is what makes the server keep it. Removing a tile only drops the
    id -- the server redacts photos no item references at submit. The photo is
    shown back as taken; nothing reads it. No `capture` attribute, so the
    client can choose the camera or an existing photo. */
export function PhotoUploadField({ labelledBy, questionKey, value, onChange }: PhotoUploadFieldProps) {
  const api = useApi();
  const tracker = useTracker();
  const copy = useQuestionnaireCopy().photoUpload;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<UploadFailure | null>(null);
  const [previews, setPreviews] = useState<ReadonlyMap<string, string>>(new Map());
  const previewsRef = useRef(new Map<string, string>());
  const requested = useRef(new Set<string>());

  const ids = parsePhotoIds(value);
  const atLimit = ids.length >= QUESTIONNAIRE_PHOTOS_PER_ITEM;

  function addPreview(id: string, url: string) {
    previewsRef.current.set(id, url);
    setPreviews(new Map(previewsRef.current));
  }

  useEffect(() => {
    for (const id of ids) {
      if (requested.current.has(id)) continue;
      requested.current.add(id);
      api.questionnaires
        .photoContent(INSTRUMENT, id)
        .then((blob) => addPreview(id, URL.createObjectURL(blob)))
        .catch(() => {
          // A thumbnail that fails to load leaves its tile blank; the id and
          // the remove button still work.
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const urls = previewsRef.current;
    return () => {
      for (const url of urls.values()) URL.revokeObjectURL(url);
    };
  }, []);

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setFailure(null);
    if (atLimit) {
      setFailure("limit_reached");
      tracker.track("questionnaire.photo_upload_failed", { instrument: INSTRUMENT, reason: "limit_reached" });
      return;
    }
    setUploading(true);
    try {
      const prepared = await preparePhoto(file);
      const { id } = await api.questionnaires.uploadPhoto(INSTRUMENT, questionKey, prepared);
      requested.current.add(id);
      addPreview(id, URL.createObjectURL(prepared));
      onChange(formatPhotoIds([...ids, id]));
    } catch (err) {
      const reason = failureFor(err);
      setFailure(reason);
      tracker.track("questionnaire.photo_upload_failed", { instrument: INSTRUMENT, reason });
    } finally {
      setUploading(false);
    }
  }

  function remove(id: string) {
    setFailure(null);
    onChange(formatPhotoIds(ids.filter((existing) => existing !== id)));
    tracker.track("questionnaire.photo_removed", { instrument: INSTRUMENT, question_key: questionKey });
  }

  const errorText =
    failure === null
      ? undefined
      : failure === "not_image"
        ? copy.errors.notImage
        : failure === "too_large"
          ? copy.errors.tooLarge
          : failure === "limit_reached"
            ? copy.errors.limitReached
            : copy.errors.network;

  return (
    <div className={s.root}>
      <p className={s.hint}>{copy.hint}</p>
      {ids.length > 0 ? (
        <ul className={s.tiles} aria-labelledby={labelledBy}>
          {ids.map((id, index) => (
            <li key={id} className={s.tile}>
              {previews.get(id) ? (
                <img src={previews.get(id)} alt={copy.photoAlt(index + 1)} className={s.image} />
              ) : (
                <span className={s.placeholder} role="img" aria-label={copy.photoAlt(index + 1)} />
              )}
              <button type="button" className={s.remove} onClick={() => remove(id)} aria-label={copy.remove(index + 1)}>
                <X aria-hidden size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!atLimit ? (
        <Button
          variant="secondary"
          className={s.add}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus aria-hidden size={18} />
          {uploading ? copy.uploading : copy.add}
        </Button>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className={s.input}
        tabIndex={-1}
        aria-hidden
        onChange={handleFile}
        data-testid="photo-upload-input"
      />
      <p aria-live="polite" className={s.liveRegion}>
        {uploading ? copy.uploading : ""}
      </p>
      {errorText ? (
        <span role="alert" className={s.error}>
          {errorText}
        </span>
      ) : null}
    </div>
  );
}
